<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import ReferenceHistory from "@/components/ReferenceHistory.vue";
import ReferenceComments from "@/components/ReferenceComments.vue";
import { ArrowUpTrayIcon, PhotoIcon } from "@heroicons/vue/24/solid";
import dayjs from "dayjs";

const route = useRoute();
const router = useRouter();
const breadcrumbStore = useBreadcrumbStore();

const isTruthyActive = (value) =>
  value === true || value === 1 || value === "1";

const normalizeVehicle = (data) =>
  data
    ? {
        ...data,
        active: isTruthyActive(data.active),
      }
    : data;

const vehicle = ref(null);
const vehicleTypes = ref([]);
const editable = ref(false);
const isLoading = ref(true);
const isSaving = ref(false);
const isDeleting = ref(false);

const maintenanceRecords = ref([]);
const maintenanceLoading = ref(false);
const maintenanceSaving = ref(false);
const maintenanceFiles = ref([]);
const maintenanceFileInput = ref(null);
const vehiclePhoto = ref("");
const vehiclePhotoLoading = ref(false);
const vehiclePhotoUploading = ref(false);
const vehiclePhotoInput = ref(null);

const maintenanceCategories = [
  { value: "service", label: "Service" },
  { value: "tyre", label: "Tyre Replacement" },
  { value: "repair", label: "Repair / Replacement" },
  { value: "issue", label: "Issue / Fault" },
];

const categoryLabels = maintenanceCategories.reduce((acc, curr) => {
  acc[curr.value] = curr.label;
  return acc;
}, {});

const newMaintenance = reactive({
  category: maintenanceCategories[0].value,
  component: "",
  occurred_at: dayjs().format("YYYY-MM-DD"),
  mileage: "",
  notes: "",
});

const referenceId = computed(
  () => vehicle.value?.reference?.id ?? vehicle.value?.reference_id
);
const assignedFuelCard = computed(
  () => vehicle.value?.assigned_fuel_card ?? null
);

const loadVehicle = () => {
  isLoading.value = true;
  apiClient
    .get(`/api/vehicle/${route.params.id}`)
    .then((r) => {
      vehicle.value = normalizeVehicle(r.data);
      breadcrumbStore.setValue(vehicle.value.registrationNumber);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const loadVehicleTypes = async () => {
  const response = await apiClient.get("/api/vehicle-types");
  vehicleTypes.value = response.data ?? [];
};

const loadVehiclePhoto = async () => {
  vehiclePhotoLoading.value = true;
  try {
    const { data } = await apiClient.get(`/api/vehicle/${route.params.id}/photo`);
    vehiclePhoto.value = data?.image || "";
  } catch (error) {
    vehiclePhoto.value = "";
  } finally {
    vehiclePhotoLoading.value = false;
  }
};

const loadMaintenanceRecords = () => {
  maintenanceLoading.value = true;
  apiClient
    .get(`/api/vehicle/${route.params.id}/maintenance`)
    .then((r) => {
      const payload = Array.isArray(r.data) ? r.data : [];
      maintenanceRecords.value = payload.map((record) => ({
        ...record,
        files: Array.isArray(record?.files) ? record.files : [],
      }));
    })
    .finally(() => {
      maintenanceLoading.value = false;
    });
};

onMounted(() => {
  loadVehicle();
  loadVehicleTypes();
  loadVehiclePhoto();
  loadMaintenanceRecords();
});

const edit = () => {
  editable.value = true;
};

const saveVehicle = () => {
  if (!vehicle.value) {
    return;
  }

  isSaving.value = true;
  apiClient
    .post(`/api/vehicle/${vehicle.value.id}`, {
      _method: "PUT",
      registrationNumber: vehicle.value.registrationNumber,
      vehicle_type_id: vehicle.value.vehicle_type_id,
      active: vehicle.value.active ? 1 : 0,
      colour: vehicle.value.colour,
      engineCapacity: vehicle.value.engineCapacity,
      fuelType: vehicle.value.fuelType,
      make: vehicle.value.make,
      model: vehicle.value.model,
      motStatus: vehicle.value.motStatus,
      motExpiryDate: vehicle.value.motExpiryDate,
      taxStatus: vehicle.value.taxStatus,
      taxDueDate: vehicle.value.taxDueDate,
      yearOfManufacture: vehicle.value.yearOfManufacture,
      euroStatus: vehicle.value.euroStatus,
      dateOfLastV5Issued: vehicle.value.dateOfLastV5Issued,
    })
    .then((r) => {
      vehicle.value = normalizeVehicle(r.data);
      editable.value = false;
    })
    .finally(() => {
      isSaving.value = false;
    });
};

const deleteVehicle = async () => {
  if (!vehicle.value) return;
  if (!confirm(`Delete vehicle ${vehicle.value.registrationNumber}?`)) {
    return;
  }
  isDeleting.value = true;
  try {
    await apiClient.delete(`/api/vehicle/${vehicle.value.id}`);
    router.push({ name: "dashboard-fleet" });
  } catch (error) {
    console.error(error);
    alert("Unable to delete vehicle.");
  } finally {
    isDeleting.value = false;
  }
};

const clearMaintenanceForm = () => {
  newMaintenance.category = maintenanceCategories[0].value;
  newMaintenance.component = "";
  newMaintenance.occurred_at = dayjs().format("YYYY-MM-DD");
  newMaintenance.mileage = "";
  newMaintenance.notes = "";
  maintenanceFiles.value = [];
  if (maintenanceFileInput.value) {
    maintenanceFileInput.value.value = "";
  }
};

const onMaintenanceFilesChange = (event) => {
  maintenanceFiles.value = Array.from(event.target.files || []).filter(Boolean);
};

const saveMaintenanceRecord = () => {
  if (!vehicle.value) {
    return;
  }
  maintenanceSaving.value = true;
  const payload = new FormData();
  payload.append("category", newMaintenance.category);
  payload.append("occurred_at", newMaintenance.occurred_at);
  if (newMaintenance.component) {
    payload.append("component", newMaintenance.component);
  }
  if (newMaintenance.mileage) {
    payload.append("mileage", String(parseInt(newMaintenance.mileage, 10)));
  }
  if (newMaintenance.notes) {
    payload.append("notes", newMaintenance.notes);
  }
  maintenanceFiles.value.forEach((file) => {
    payload.append("files[]", file);
  });
  apiClient
    .post(`/api/vehicle/${vehicle.value.id}/maintenance`, payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((r) => {
      const record = {
        ...r.data,
        files: Array.isArray(r.data?.files) ? r.data.files : [],
      };
      maintenanceRecords.value = [record, ...maintenanceRecords.value];
      clearMaintenanceForm();
    })
    .catch((error) => {
      alert(error?.response?.data?.message ?? "Unable to save maintenance record.");
    })
    .finally(() => {
      maintenanceSaving.value = false;
    });
};

const openMaintenanceFile = async (file) => {
  try {
    const response = await apiClient.get(`/api/files/${file.id}`, {
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.target = "_blank";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to open file.");
  }
};

const openVehiclePhotoPicker = () => {
  vehiclePhotoInput.value?.click();
};

const prepareVehiclePhotoForUpload = async (file) => {
  if (!(file instanceof File) || !file.type.startsWith("image/")) {
    throw new Error("Please select a valid image file.");
  }

  const blobUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Unable to read this image."));
      img.src = blobUrl;
    });

    const maxDimension = 1600;
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to process this image.");
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const outputBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Unable to process this image."));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.86,
      );
    });

    const baseName = (file.name || "vehicle-photo").replace(/\.[^.]+$/, "");
    return new File([outputBlob], `${baseName}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
};

const onVehiclePhotoSelected = async (event) => {
  const selectedFile = event.target.files?.[0];
  if (!selectedFile || !vehicle.value?.id) {
    return;
  }

  let uploadFile;
  try {
    uploadFile = await prepareVehiclePhotoForUpload(selectedFile);
  } catch (error) {
    alert(error?.message || "Unable to process image.");
    if (event.target) {
      event.target.value = "";
    }
    return;
  }

  const payload = new FormData();
  payload.append("photo", uploadFile);
  vehiclePhotoUploading.value = true;
  try {
    await apiClient.post(`/api/vehicle/${vehicle.value.id}/photo`, payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    await loadVehiclePhoto();
    await loadVehicle();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.photo?.[0] ||
      error?.response?.data?.message ||
      "Unable to upload vehicle photo.";
    alert(message);
  } finally {
    vehiclePhotoUploading.value = false;
    if (event.target) {
      event.target.value = "";
    }
  }
};

const truncateFilename = (value, maxLength = 28) => {
  const filename = String(value || "");
  if (!filename || filename.length <= maxLength) {
    return filename || "File";
  }

  const extensionIndex = filename.lastIndexOf(".");
  if (extensionIndex > 0) {
    const extension = filename.slice(extensionIndex);
    const hasShortExtension = extension.length <= 8;
    const headLength = maxLength - extension.length - 3;
    if (hasShortExtension && headLength > 6) {
      return `${filename.slice(0, headLength)}...${extension}`;
    }
  }

  return `${filename.slice(0, maxLength - 3)}...`;
};

const formatDate = (value) =>
  value ? dayjs(value).format("DD MMM YYYY") : "—";

const latestByCategory = (category) =>
  maintenanceRecords.value.find((record) => record.category === category);

const lastService = computed(() => latestByCategory("service"));
const lastTyre = computed(() => latestByCategory("tyre"));
const lastRepair = computed(() => latestByCategory("repair"));
const lastIssue = computed(() => latestByCategory("issue"));
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <router-link to="/dashboard/fleet" class="btn w-full text-center md:w-auto">
        Back to Vehicles
      </router-link>
      <div v-if="vehicle" class="flex gap-2 justify-end flex-wrap">
        <button
          class="btn danger text-xs"
          @click.prevent="deleteVehicle"
          :disabled="isDeleting"
        >
          {{ isDeleting ? "Deleting..." : "Delete" }}
        </button>
        <edit-button v-if="!editable" @click.prevent="edit" />
        <save-button
          v-else
          @save="saveVehicle"
          :processing="isSaving"
          :disabled="isSaving"
        />
      </div>
    </div>

    <div v-if="vehicle" class="space-y-4">
      <section
        class="overflow-hidden rounded-2xl border border-gray-100 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <div class="h-20 bg-slate-100 dark:bg-slate-700/40"></div>
        <div class="-mt-10 px-4 pb-4 sm:px-5">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div class="flex min-w-0 items-end gap-4">
              <div
                class="relative h-28 w-28 shrink-0 overflow-hidden rounded-full border-4 border-white bg-slate-50 shadow-sm dark:border-slate-800 dark:bg-slate-900/40"
              >
                <img
                  v-if="vehiclePhoto"
                  :src="vehiclePhoto"
                  :alt="`Vehicle photo for ${vehicle.registrationNumber}`"
                  class="h-full w-full object-cover"
                  @error="vehiclePhoto = ''"
                />
                <div
                  v-else
                  class="flex h-full w-full items-center justify-center text-slate-500 dark:text-slate-400"
                >
                  <PhotoIcon class="h-10 w-10 opacity-70" />
                </div>
                <div
                  v-if="vehiclePhotoLoading || vehiclePhotoUploading"
                  class="absolute inset-0 flex items-center justify-center bg-slate-900/40 text-[11px] font-medium text-white"
                >
                  {{ vehiclePhotoUploading ? "Uploading..." : "Loading..." }}
                </div>
              </div>
              <div class="min-w-0 pb-1">
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Vehicle Profile
                </p>
                <h2 class="truncate text-2xl font-semibold text-slate-900 dark:text-slate-100">
                  {{ vehicle.registrationNumber }}
                </h2>
                <p class="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">
                  {{ vehicle.make || "—" }} {{ vehicle.model || "" }}
                  <span v-if="vehicle.fuelType">• {{ vehicle.fuelType }}</span>
                </p>
                <p class="mt-1 text-xs font-medium text-cyan-700 dark:text-cyan-300">
                  {{ vehicle.vehicle_type?.name || "Vehicle type not assigned" }}
                </p>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <input
                ref="vehiclePhotoInput"
                type="file"
                accept="image/*"
                class="hidden"
                :disabled="vehiclePhotoUploading"
                @change="onVehiclePhotoSelected"
              />
              <button
                type="button"
                class="btn text-xs inline-flex items-center gap-1"
                :disabled="vehiclePhotoUploading"
                @click="openVehiclePhotoPicker"
              >
                <ArrowUpTrayIcon class="h-4 w-4" />
                <span>{{ vehiclePhoto ? "Change Photo" : "Upload Photo" }}</span>
              </button>
              <span
                class="inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold"
                :class="vehicle.active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-700/30 dark:text-emerald-200' : 'bg-rose-100 text-rose-700 dark:bg-rose-700/30 dark:text-rose-200'"
              >
                {{ vehicle.active ? "Active" : "Inactive" }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:bg-slate-800 dark:border-slate-700">
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Make & Model</div>
          <div class="text-lg font-semibold mt-1">
            {{ vehicle.make }} <span class="text-gray-400" v-if="vehicle.model">• {{ vehicle.model }}</span>
          </div>
          <div class="text-sm text-gray-500 dark:text-gray-400">Colour: {{ vehicle.colour || "—" }}</div>
        </div>
        <div class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:bg-slate-800 dark:border-slate-700">
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Fuel & Capacity</div>
          <div class="text-lg font-semibold mt-1">
            {{ vehicle.fuelType || "—" }}
          </div>
          <div class="text-sm text-gray-500 dark:text-gray-400">
            Engine: {{ vehicle.engineCapacity || "—" }}
          </div>
        </div>
        <div class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:bg-slate-800 dark:border-slate-700">
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Compliance</div>
          <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            MOT Status: <span class="font-semibold text-slate-900 dark:text-slate-100">{{ vehicle.motStatus || "—" }}</span>
          </div>
          <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Last MOT: <span class="font-semibold text-slate-900 dark:text-slate-100">{{ formatDate(vehicle.motExpiryDate) }}</span>
          </div>
        </div>
        <div class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:bg-slate-800 dark:border-slate-700">
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Fuel Card</div>
          <router-link
            v-if="assignedFuelCard"
            :to="{ name: 'fuel-cards-view', params: { id: assignedFuelCard.id } }"
            class="mt-1 inline-flex flex-col rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-cyan-700 transition-colors hover:bg-cyan-100 dark:border-cyan-700/50 dark:bg-cyan-900/30 dark:text-cyan-200 dark:hover:bg-cyan-900/40"
          >
            <span class="text-sm font-semibold">
              {{ assignedFuelCard.card_name || assignedFuelCard.card_number }}
            </span>
            <span class="text-xs opacity-80">
              {{ assignedFuelCard.card_number }}
            </span>
            <span class="text-xs opacity-80">
              Expires {{ formatDate(assignedFuelCard.expiry_date) }}
            </span>
          </router-link>
          <div
            v-else
            class="mt-2 text-sm text-gray-500 dark:text-gray-400"
          >
            No fuel card assigned.
          </div>
        </div>
      </div>
    </div>

    <form-box header="Vehicle Details">
      <div v-if="isLoading">Loading vehicle...</div>
      <div v-else-if="vehicle" class="space-y-6">
        <div class="grid gap-6 lg:grid-cols-2">
          <section class="space-y-4 rounded-2xl border border-gray-100 p-4 shadow-sm dark:border-slate-700">
            <h3 class="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400">
              Vehicle Info
            </h3>
            <div class="grid gap-4 sm:grid-cols-2">
              <label class="block sm:col-span-2">
                <span>Registration Number</span>
                <input
                  type="text"
                  v-model="vehicle.registrationNumber"
                  :disabled="!editable"
                  class="uppercase mt-1 w-full"
                />
              </label>
              <label class="block">
                <span>Status</span>
                <div class="flex items-center mt-1 gap-2">
                  <input
                    type="checkbox"
                    v-model="vehicle.active"
                    :disabled="!editable"
                  />
                  <span>{{ vehicle.active ? "Active" : "Inactive" }}</span>
                </div>
              </label>
              <label class="block">
                <span>Vehicle Type</span>
                <select
                  v-model="vehicle.vehicle_type_id"
                  :disabled="!editable"
                  class="mt-1 w-full"
                >
                  <option value="" disabled>Select a type</option>
                  <option
                    v-for="type in vehicleTypes"
                    :key="type.id"
                    :value="type.id"
                    :disabled="!type.active && Number(vehicle.vehicle_type_id) !== type.id"
                  >
                    {{ type.name }}{{ type.active ? "" : " (inactive)" }}
                  </option>
                </select>
              </label>
              <label class="block">
                <span>Make</span>
                <input type="text" v-model="vehicle.make" :disabled="!editable" class="mt-1 w-full" />
              </label>
              <label class="block">
                <span>Model</span>
                <input type="text" v-model="vehicle.model" :disabled="!editable" class="mt-1 w-full" />
              </label>
              <label class="block">
                <span>Colour</span>
                <input type="text" v-model="vehicle.colour" :disabled="!editable" class="mt-1 w-full" />
              </label>
              <label class="block">
                <span>Engine Capacity</span>
                <input
                  type="text"
                  v-model="vehicle.engineCapacity"
                  :disabled="!editable"
                  class="mt-1 w-full"
                />
              </label>
              <label class="block">
                <span>Fuel Type</span>
                <input type="text" v-model="vehicle.fuelType" :disabled="!editable" class="mt-1 w-full" />
              </label>
              <label class="block">
                <span>Euro Status</span>
                <input type="text" v-model="vehicle.euroStatus" :disabled="!editable" class="mt-1 w-full" />
              </label>
            </div>
          </section>

          <section class="space-y-4 rounded-2xl border border-gray-100 p-4 shadow-sm dark:border-slate-700">
            <h3 class="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400">
              Compliance & Dates
            </h3>
            <div class="grid gap-4 sm:grid-cols-2">
              <label class="block">
                <span>Year of Manufacture</span>
                <input
                  type="number"
                  v-model="vehicle.yearOfManufacture"
                  :disabled="!editable"
                  min="1900"
                  max="2100"
                  class="mt-1 w-full"
                />
              </label>
              <label class="block">
                <span>MOT Status</span>
                <input type="text" v-model="vehicle.motStatus" :disabled="!editable" class="mt-1 w-full" />
              </label>
              <label class="block">
                <span>MOT Expiry Date</span>
                <input
                  type="date"
                  v-model="vehicle.motExpiryDate"
                  :disabled="!editable"
                  class="mt-1 w-full"
                />
              </label>
              <label class="block">
                <span>Tax Status</span>
                <input type="text" v-model="vehicle.taxStatus" :disabled="!editable" class="mt-1 w-full" />
              </label>
              <label class="block">
                <span>Tax Due Date</span>
                <input
                  type="date"
                  v-model="vehicle.taxDueDate"
                  :disabled="!editable"
                  class="mt-1 w-full"
                />
              </label>
              <label class="block">
                <span>Date of Last V5 Issued</span>
                <input
                  type="date"
                  v-model="vehicle.dateOfLastV5Issued"
                  :disabled="!editable"
                  class="mt-1 w-full"
                />
              </label>
            </div>
          </section>
        </div>
      </div>
      <div v-else>No vehicle data available.</div>
    </form-box>

    <form-box header="Maintenance & Issues">
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div class="uppercase text-xs text-gray-500 dark:text-gray-400 mb-1">Last Service</div>
          <div class="font-semibold">
            {{ formatDate(lastService?.occurred_at) }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400" v-if="lastService">
            {{ lastService.component || "Full Service" }}
          </div>
        </div>
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div class="uppercase text-xs text-gray-500 dark:text-gray-400 mb-1">Last Tyre</div>
          <div class="font-semibold">
            {{ formatDate(lastTyre?.occurred_at) }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400" v-if="lastTyre?.component">
            {{ lastTyre.component }}
          </div>
        </div>
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div class="uppercase text-xs text-gray-500 dark:text-gray-400 mb-1">Last Repair</div>
          <div class="font-semibold">
            {{ formatDate(lastRepair?.occurred_at) }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400" v-if="lastRepair?.component">
            {{ lastRepair.component }}
          </div>
        </div>
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div class="uppercase text-xs text-gray-500 dark:text-gray-400 mb-1">Latest Issue</div>
          <div class="font-semibold">
            {{ formatDate(lastIssue?.occurred_at) }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400" v-if="lastIssue?.notes">
            {{ lastIssue.notes }}
          </div>
        </div>
      </div>

      <div class="mb-6 rounded-2xl border border-dashed border-gray-200 p-4 shadow-sm dark:border-slate-700">
        <h3 class="font-semibold mb-3">Log Maintenance / Issues</h3>
        <div class="grid gap-4 sm:grid-cols-2">
          <div>
            <label>Type</label>
            <select v-model="newMaintenance.category" class="w-full">
              <option
                v-for="option in maintenanceCategories"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div>
            <label>Component / Location</label>
            <input type="text" v-model="newMaintenance.component" class="w-full" />
          </div>
          <div>
            <label>Date</label>
            <input type="date" v-model="newMaintenance.occurred_at" class="w-full" />
          </div>
          <div>
            <label>Mileage</label>
            <input type="number" min="0" v-model="newMaintenance.mileage" class="w-full" />
          </div>
          <div class="sm:col-span-2">
            <label>Notes</label>
            <textarea
              class="w-full min-h-24"
              v-model="newMaintenance.notes"
            ></textarea>
          </div>
          <div class="sm:col-span-2">
            <label>Attachments (optional)</label>
            <input
              ref="maintenanceFileInput"
              type="file"
              multiple
              class="mt-1 w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800 dark:text-slate-300"
              :disabled="maintenanceSaving"
              @change="onMaintenanceFilesChange"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              You can attach one or more files to this maintenance record.
            </p>
            <div
              v-if="maintenanceFiles.length"
              class="mt-2 flex flex-wrap gap-2"
            >
              <span
                v-for="(file, index) in maintenanceFiles"
                :key="`${file.name}-${index}`"
                class="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-100"
              >
                {{ file.name }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end mt-3">
          <save-button
            class="w-full sm:w-auto"
            @save="saveMaintenanceRecord"
            :processing="maintenanceSaving"
            :disabled="maintenanceSaving"
          />
        </div>
      </div>

      <div>
        <h3 class="font-semibold mb-3">Maintenance History</h3>
        <div v-if="maintenanceLoading">Loading maintenance history...</div>
        <div v-else-if="!maintenanceRecords.length">
          No maintenance entries recorded yet.
        </div>
        <div v-else>
          <div class="hidden md:block overflow-x-auto rounded-2xl border border-gray-100 shadow-sm dark:border-slate-700">
            <table class="w-full text-left text-sm">
              <thead class="bg-gray-50 dark:bg-slate-900/50">
                <tr>
                  <th class="p-3">Date</th>
                  <th class="p-3">Type</th>
                  <th class="p-3">Component</th>
                  <th class="p-3">Details</th>
                  <th class="p-3">Mileage</th>
                  <th class="p-3">Files</th>
                  <th class="p-3">Logged By</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="record in maintenanceRecords"
                  :key="record.id"
                  class="border-t border-gray-100 dark:border-slate-700"
                >
                  <td class="p-3 whitespace-nowrap">{{ formatDate(record.occurred_at) }}</td>
                  <td class="p-3">
                    <span
                      class="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100"
                    >
                      {{ categoryLabels[record.category] ?? record.category }}
                    </span>
                  </td>
                  <td class="p-3 whitespace-nowrap">{{ record.component || "—" }}</td>
                  <td class="p-3 whitespace-pre-wrap">
                    {{ record.notes || "—" }}
                  </td>
                  <td class="p-3">
                    {{ record.mileage ? record.mileage + " mi" : "—" }}
                  </td>
                  <td class="p-3">
                    <div
                      v-if="record.files?.length"
                      class="flex flex-wrap gap-2"
                    >
                      <button
                        v-for="file in record.files"
                        :key="file.id"
                        type="button"
                        class="text-xs font-semibold text-cyan-600 hover:underline dark:text-cyan-400"
                        :title="file.filename"
                        @click="openMaintenanceFile(file)"
                      >
                        <span class="inline-block max-w-[12rem] truncate align-bottom">
                          {{ truncateFilename(file.filename) }}
                        </span>
                      </button>
                    </div>
                    <span v-else class="text-xs text-gray-500 dark:text-gray-400">
                      —
                    </span>
                  </td>
                  <td class="p-3">
                    {{ record.user?.name ?? "Unknown" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="space-y-3 md:hidden">
            <article
              v-for="record in maintenanceRecords"
              :key="record.id"
              class="rounded-2xl border border-gray-100 p-4 shadow-sm dark:border-slate-700"
            >
              <div class="flex items-center justify-between text-xs uppercase text-gray-500 dark:text-gray-400 mb-2">
                <span>{{ formatDate(record.occurred_at) }}</span>
                <span
                  class="inline-flex items-center px-2 py-1 rounded text-[10px] bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100"
                >
                  {{ categoryLabels[record.category] ?? record.category }}
                </span>
              </div>
              <div class="text-sm font-semibold">
                {{ record.component || "—" }}
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 mt-2 whitespace-pre-wrap">
                {{ record.notes || "No additional information" }}
              </p>
              <div class="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400 mt-3">
                <span>Mileage: {{ record.mileage ? record.mileage + " mi" : "—" }}</span>
                <span>Logged by: {{ record.user?.name ?? "Unknown" }}</span>
              </div>
              <div v-if="record.files?.length" class="mt-3 flex flex-wrap gap-2">
                <button
                  v-for="file in record.files"
                  :key="file.id"
                  type="button"
                  class="text-xs font-semibold text-cyan-600 hover:underline dark:text-cyan-400"
                  :title="file.filename"
                  @click="openMaintenanceFile(file)"
                >
                  <span class="inline-block max-w-[12rem] truncate align-bottom">
                    {{ truncateFilename(file.filename) }}
                  </span>
                </button>
              </div>
            </article>
          </div>
        </div>
      </div>
    </form-box>

    <div class="grid md:grid-cols-2 gap-4" v-if="referenceId">
      <ReferenceHistory :reference_id="referenceId" />
      <ReferenceComments :reference_id="referenceId" />
    </div>
  </div>
</template>
